import React, { useState } from "react";
import styled from "styled-components";
import AddToCartControls from "../../AddToCartControls";
import Flex from "../../ui/Flex";
import Stack from "../../ui/Stack";
import Text from "../../ui/Text";
import TextCircleDivider from "../../ui/TextCircleDivider";
import Icon from "../../ui/Icon";
import Note from "../../ui/Note";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalImage, ModalTitle } from "../../ui/Modal";
import FormatPrice from "../../formatters/FormatPrice";
import FormatDuration from "../../formatters/FormatDuration";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";
import { htmlToPlainText, sanitizeHtml } from "../../../utils/sanitize";

const StyledAddonListItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  padding: 1rem;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Media = styled(Flex)`
  width: 56px;
  height: 56px;
  border-radius: 0.5rem;
  flex-shrink: 0;
  overflow: hidden;
  background-color: rgba(${({ theme }) => theme.colors.border.rgb}, 0.5);
`;

const ContentRow = styled(Flex)`
  min-width: 0;
`;

const TextStack = styled(Stack)`
  flex: 1;
  min-width: 0;
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoteRow = styled.div`
  display: grid;
  grid-template-rows: ${({ $visible }) => ($visible ? "1fr" : "0fr")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: grid-template-rows 0.3s ease, opacity 0.3s ease;
`;

const NoteRowInner = styled.div`
  overflow: hidden;
`;

const NoteRowContent = styled.div`
  padding: 0 1rem 1rem;
`;

const AddonListItem = ({ product, position, selectedProducts, handleProductSelect, updateProduct, canEditQty = false, addedLabel, noteText, isNoteVisible = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const image = useAssetsSrc(product.picture_path);
  const isSelected = selectedProducts?.find((s) => s.id === product.id);
  const inCart = Boolean(isSelected);
  const hasPhoto = product.picture_path && !product.picture_path.endsWith(".svg");

  const modalHeading = (
    <Flex $justify="space-between" $gap="1rem">
      <ModalTitle>{product.name}</ModalTitle>
      {!!product.price && (
        <Text size="xlarge" weight="bold" color="dark">
          <FormatPrice value={product.price} currency={product.currency} />
        </Text>
      )}
    </Flex>
  );

  const controls = (
    <AddToCartControls
      inCart={inCart}
      qty={isSelected?.qty || 0}
      canEditQty={canEditQty}
      addedLabel={addedLabel}
      onAdd={() => handleProductSelect(position, product)}
      onUpdate={(newQty) => updateProduct(position, product, newQty)}
      onRemove={() => handleProductSelect(position, product)}
    />
  );

  return (
    <>
      <StyledAddonListItem>
        <ContentRow $gap="1rem">
          <Media $align="center" $justify="center">
            {product.picture_path
              ? <MediaImage src={image} alt={product.name} />
              : <Icon.Blocks size={Icon.SIZE.MD} color="iconStroke" strokeWidth={1.3} />}
          </Media>

          <TextStack $gap="0.25rem">
            <Text color="dark" weight="bold">{product.name}</Text>
            {!!product.description && (
              <Text size="small" lineClamp={2} onReadMoreClick={() => setIsModalOpen(true)}>
                {htmlToPlainText(product.description)}
              </Text>
            )}
            <Flex $align="center" $gap="0.25rem">
              {product.price
                ? (
                  <Text color="dark" weight="bold">
                    <FormatPrice value={product.price} currency={product.currency} />
                  </Text>
                )
                : <Text color="dark" weight="bold">Free</Text>}
              {!!product.duration && (
                <>
                  <TextCircleDivider size={4} />
                  <Text>
                    <FormatDuration value={product.duration} type={FormatDuration.TYPE.HOUR} />
                  </Text>
                </>
              )}
            </Flex>
          </TextStack>
        </ContentRow>

        {controls}
      </StyledAddonListItem>

      {noteText && (
        <NoteRow $visible={isNoteVisible} aria-hidden={!isNoteVisible}>
          <NoteRowInner>
            <NoteRowContent>
              <Note text={noteText} />
            </NoteRowContent>
          </NoteRowInner>
        </NoteRow>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          {product.picture_path ? (
            <>
              <ModalCloseButton />
              <ModalImage src={image} alt={product.name} />
            </>
          ) : (
            <ModalHeader>{modalHeading}</ModalHeader>
          )}
          <ModalBody>
            <Flex $direction="column" $gap="1rem">
              {(hasPhoto || product.description) && (
                <Stack $gap="0.5rem">
                  {hasPhoto && modalHeading}
                  {product.description && <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} />}
                </Stack>
              )}
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddonListItem;
